import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { RevealLines } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const SpinBadge = () => (
  <div className="absolute -left-8 top-10 z-20 h-28 w-28 md:-left-14 md:h-36 md:w-36" style={{ transform: "translateZ(80px)" }}>
    <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
      <defs><path id="circ" d="M50 50 m-38 0 a38 38 0 1 1 76 0 a38 38 0 1 1 -76 0" /></defs>
      <circle cx="50" cy="50" r="50" fill="#FF007A" />
      <text fill="#fff" fontFamily="JetBrains Mono" fontSize="8.4" fontWeight="700">
        <textPath href="#circ" textLength="236" lengthAdjust="spacing">NEW DROP LIVE • NEW DROP LIVE •</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 flex items-center justify-center font-display text-2xl font-extrabold">IC</span>
  </div>
);

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto w-[78%] max-w-[460px]" style={{ perspective: 1200 }}>
      <div className="absolute -inset-10 rounded-full bg-violet/60 blur-[90px]" />
      <div className="absolute -bottom-10 left-1/2 h-24 w-[90%] -translate-x-1/2 rounded-full bg-pink/40 blur-3xl" />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 40px 40px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 40px 40px)" }}
          transition={{ duration: 1.4, ease, delay: 0.5 }}
          className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[40px] border-2 border-lime/70"
        >
          <motion.img initial={{ scale: 1.35 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease, delay: 0.5 }}
            src="/img/burning-fire.jpg" alt="Burning Fire flame print socks" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_30%,rgba(11,1,24,0.65)_100%)]" />
        </motion.div>
        <SpinBadge />
        <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.7, ease }}
          style={{ transform: "translateZ(60px)" }}
          className="absolute -right-4 bottom-16 rotate-6 rounded-full bg-lime px-4 py-2 font-mono text-xs font-bold uppercase text-ink md:-right-10">
          Burning Fire · ₹299
        </motion.span>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden px-5 pb-20 pt-28 md:px-10 md:pt-36 lg:min-h-screen">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(139,0,255,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(139,0,255,0.12)_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="relative mx-auto grid max-w-[1440px] items-center gap-16 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-lime">
            <span className="h-2 w-2 animate-pulse rounded-full bg-pink" /> Drop 01 · Now live
          </motion.p>
          <RevealLines
            testId="hero-title"
            className="font-display text-[clamp(3rem,12.5vw,6rem)] lg:text-[7.2vw] 2xl:text-[8.5rem] font-extrabold uppercase leading-[0.86] tracking-[-0.045em]"
            lines={["Let's go ", <span key="b" className="text-lime">Insane</span>]}
          />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8, ease }}
            className="mt-10">
            <motion.button data-testid="hero-shop-cta" whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }} onClick={() => scrollToId("shop")}
              className="group flex w-fit items-center gap-3 rounded-full bg-lime py-4 pl-7 pr-4 font-display text-lg font-extrabold uppercase text-ink">
              Shop the drop
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-lime transition-transform duration-300 group-hover:rotate-[-45deg]">
                <ArrowDownRight className="h-5 w-5" />
              </span>
            </motion.button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: visualY }} className="lg:col-span-5">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};
